import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  styled,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  LocalOffer as LocalOfferIcon,
  Assessment as AssessmentIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Receipt as ReceiptIcon,
  Percent as PercentIcon,
  Calculate as CalculateIcon,
  WarningAmber as WarningAmberIcon,
  SchoolOutlined as SchoolOutlinedIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import ModalServicio from './ModalServicio';

const RISK_LEVEL_LABELS = { BAJO: 'Bajo', MEDIO: 'Medio', ALTO: 'Alto' };
const RISK_LEVEL_COLORS = {
  BAJO: { bg: '#e8f5e9', text: '#2e7d32' },
  MEDIO: { bg: '#fff3e0', text: '#e65100' },
  ALTO: { bg: '#ffebee', text: '#c62828' }
};

const StyledTableRow = styled(TableRow)({
  '&.selected': {
    backgroundColor: '#ADD8E6 !important',
    '&:hover': {
      backgroundColor: '#ADD8E6 !important'
    }
  }
});

const CatalogoServicios = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    avgPrice: 0
  });
  const [filters, setFilters] = useState({
    serviceName: '',
    folioPrefix: '',
    priceRange: '',
    active: '' // Vacío por defecto para mostrar TODOS los servicios
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [editService, setEditService] = useState({
    serviceName: '',
    folioPrefix: '',
    price: '',
    discount: '',
    active: true,
    iva: 16, // IVA fijo 16%
    subtotal: '',
    riskLevel: ''
  });

  // Resetear el tab del modal cuando no hay servicio seleccionado
  useEffect(() => {
    if (!selectedService) {
      setSelectedTab(0);
    }
  }, [selectedService]);

  const resetForm = () => {
    setEditService({
      serviceName: '',
      folioPrefix: '',
      price: '',
      discount: '',
      active: true,
      iva: 16, // IVA fijo 16%
      subtotal: '',
      riskLevel: ''
    });
  };
  
  // Calcular subtotal automáticamente cuando cambian precio o descuento
  const calculateSubtotal = (price, discount) => {
    const priceNum = parseFloat(price) || 0;
    const discountNum = parseFloat(discount) || 0;
    const discountAmount = priceNum * (discountNum / 100);
    const subtotal = priceNum - discountAmount;
    return subtotal.toFixed(2);
  };

  const calculateStats = (data) => {
    const stats = {
      total: data.length,
      active: data.filter(s => s.active).length,
      inactive: data.filter(s => !s.active).length,
      avgPrice: data.length > 0 ? data.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0) / data.length : 0
    };
    setStats(stats);
  };

  // Filtrar servicios
  useEffect(() => {
    console.log('🔄 useEffect de filtros ejecutándose...');
    console.log('📋 Services disponibles:', services.length);
    console.log('🎛️ Filtros actuales:', filters);
    
    let filtered = services;

    if (filters.serviceName) {
      console.log('🔍 Aplicando filtro por nombre:', filters.serviceName);
      filtered = filtered.filter(s => 
        s.serviceName?.toLowerCase().includes(filters.serviceName.toLowerCase())
      );
    }

    if (filters.folioPrefix) {
      console.log('🔍 Aplicando filtro por prefijo:', filters.folioPrefix);
      filtered = filtered.filter(s => 
        s.folioPrefix?.toLowerCase().includes(filters.folioPrefix.toLowerCase())
      );
    }

    if (filters.active !== '') {
      console.log('🔍 Aplicando filtro por estado activo:', filters.active);
      filtered = filtered.filter(s => s.active === (filters.active === 'true'));
    }

    if (filters.priceRange) {
      console.log('🔍 Aplicando filtro por rango de precio:', filters.priceRange);
      const [min, max] = filters.priceRange.split('-').map(Number);
      filtered = filtered.filter(s => {
        const price = parseFloat(s.price) || 0;
        return price >= min && (max ? price <= max : true);
      });
    }

    console.log('✅ Servicios después de filtros:', filtered.length);
    console.log('📊 Servicios filtrados:', filtered.map(s => ({ id: s.id, name: s.serviceName, active: s.active })));
    
    setFilteredServices(filtered);
  }, [filters.serviceName, filters.folioPrefix, filters.active, filters.priceRange, services]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/services');

      const servicesData = response.data;
      console.log('🔍 TODOS los servicios del backend:', servicesData);
      console.log('📊 Total servicios recibidos:', servicesData.length);
      console.log('❌ Servicios INACTIVOS (active=false):', servicesData.filter(s => s.active === false));
      console.log('✅ Servicios ACTIVOS (active=true):', servicesData.filter(s => s.active === true));
      console.log('⚠️ Servicios con active undefined/null:', servicesData.filter(s => s.active === undefined || s.active === null));

      // Forzar que todos los servicios se muestren sin filtros
      setServices(servicesData);
      setFilteredServices(servicesData);
      calculateStats(servicesData);
      
      console.log('✨ Estado actualizado - services:', servicesData.length, 'filteredServices:', servicesData.length);
    } catch (err) {
      setError('Error al cargar los servicios');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Abre el modal unificado de edición (Información General + Subservicios + Cobertura +
  // Capacitaciones por Defecto) en su primer tab. Antes abría un diálogo aparte con solo
  // nombre/prefijo/precio/descuento -- ahora todo vive en un solo lugar (ModalServicio).
  const handleEditClick = (service) => {
    setSelectedService(service);
    setSelectedTab(0);
    setModalOpen(true);
  };

  const clearEditServiceParam = () => {
    const params = new URLSearchParams(location.search);
    if (!params.has('editService')) return;

    params.delete('editService');
    const nextSearch = params.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
    navigate(nextUrl, { replace: true });
  };

  const handleRowClick = (service) => {
    //console.log('Servicio seleccionado:', service);
    
    // Mostrar alert con los datos del servicio
    /*
    if (service) {
      const dataString = JSON.stringify(service, null, 2);
      window.alert(`Datos del servicio seleccionado:\n${dataString}`);
    }
      */
    setSelectedService(service);
    setSelectedTab(0);
    setModalOpen(true);
  };

  const handleRowHover = (service) => {
    // Solo mostramos el servicio al hacer hover si no hay uno seleccionado
   // if (!selectedService) {
   //     setSelectedService(service);
   // }
  };

  const handleCellClick = (event) => {
     // Evitar que el click en botones de acción active handleRowClick
  if (event.target.closest('button')) return;
  const serviceId = event.currentTarget.getAttribute('data-service-id');
  const service = services.find(s => String(s.id) === String(serviceId));
  if (service) {
    handleRowClick(service);
  }
  };

  const validateService = () => {
    const errors = [];
    
    if (!editService.serviceName) errors.push('El nombre del servicio es requerido');
    if (!editService.folioPrefix) errors.push('El prefijo del folio es requerido');
    if (!editService.price) errors.push('El precio es requerido');
    if (editService.active === undefined) errors.push('El estado activo es requerido');

    // Validar que los campos numéricos sean números válidos
    if (editService.price && isNaN(parseFloat(editService.price))) {
      errors.push('El precio debe ser un número válido');
    }
    if (editService.discount && isNaN(parseFloat(editService.discount))) {
      errors.push('El descuento debe ser un número válido');
    }

    return errors;
  };

  const handleCreateSubmit = async () => {
    try {
      const validationErrors = validateService();
      if (validationErrors.length > 0) {
        setError(validationErrors.join('\n'));
        return;
      }

      const subtotal = calculateSubtotal(editService.price, editService.discount);
      
      const serviceData = {
        serviceName: editService.serviceName,
        folioPrefix: editService.folioPrefix,
        price: parseFloat(editService.price) || 0,
        discount: parseFloat(editService.discount) || 0,
        active: editService.active !== undefined ? editService.active : true,
        iva: 16.0, // IVA fijo 16%
        subtotal: parseFloat(subtotal) || 0.0,
        riskLevel: editService.riskLevel || null
      };

      const response = await api.post('/services', serviceData);
      const newServices = [...services, response.data];
      setServices(newServices);
      setFilteredServices(newServices);
      calculateStats(newServices);
      setCreateDialogOpen(false);
      setError(null);
      resetForm();
    } catch (err) {
      setError('Error al crear el servicio');
      console.error('Error:', err);
    }
  };

  const handleDeleteClick = async (service) => {
    try {
      if (window.confirm('¿Estás seguro de que deseas eliminar este servicio?')) {
        await api.delete(`/services/${service.id}`);
        const updatedServices = services.filter(s => s.id !== service.id);
        setServices(updatedServices);
        setFilteredServices(updatedServices);
        calculateStats(updatedServices);
        setError(null); // Reset error after successful delete
      }
    } catch (err) {
      setError('Error al eliminar el servicio');
      console.error('Error:', err);
    }
  };

  const handleToggleActive = async (service) => {
    try {
      console.log('Toggle clicked for service:', service);
      console.log('Current active state:', service.active);
      
      // Mostrar confirmación antes de cambiar el estado
      const currentState = service.active ? 'activo' : 'inactivo';
      const newState = service.active ? 'inactivo' : 'activo';
      const confirmMessage = `¿Desea cambiar el servicio "${service.serviceName}" de ${currentState} a ${newState}?`;
      
      const confirmed = window.confirm(confirmMessage);
      
      if (!confirmed) {
        console.log('Usuario canceló el cambio de estado');
        return; // No hacer nada si el usuario cancela
      }
      
      const updatedServiceData = {
        serviceName: service.serviceName,
        folioPrefix: service.folioPrefix,
        price: parseFloat(service.price) || 0,
        discount: parseFloat(service.discount) || 0,
        iva: parseFloat(service.iva) || 0.0,
        subtotal: parseFloat(service.subtotal) || 0.0,
        active: !service.active
      };
      
      console.log('Sending data to backend:', updatedServiceData);
      
      const response = await api.put(`/services/${service.id}`, updatedServiceData);
      console.log('Backend response:', response.data);
      
      // Usar directamente la respuesta del backend
      console.log('🔄 Servicio original:', service);
      console.log('🔄 Respuesta del backend:', response.data);
      
      const updatedServices = services.map(s => 
        s.id === service.id ? { ...response.data } : s
      );
      
      const updatedFilteredServices = filteredServices.map(s => 
        s.id === service.id ? { ...response.data } : s
      );
      
      console.log('✅ Actualizando estados con respuesta del backend...');
      console.log('📊 Nuevo estado active:', response.data.active);
      
      setServices([...updatedServices]);
      setFilteredServices([...updatedFilteredServices]);
      calculateStats(updatedServices);
      setError(null);
      
      console.log('Services updated successfully');
    } catch (err) {
      setError('Error al cambiar el estado del servicio');
      console.error('Error details:', err);
      console.error('Error response:', err.response?.data);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // Detectar parámetro editService en la URL y abrir modal de edición
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editServiceId = params.get('editService');
    
    if (editServiceId && services.length > 0) {
      const serviceToEdit = services.find(service => service.id.toString() === editServiceId);
      if (serviceToEdit) {
        handleEditClick(serviceToEdit);
      }
    }
  }, [location.search, services]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
  <Box p={3}>
    <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
      Catálogo de Servicios
    </Typography>

    {/* Cards de Estadísticas */}
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
        <Card 
          sx={{ 
            height: '120px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              boxShadow: 3,
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}
        >
          <CardContent sx={{ 
            textAlign: 'center', 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <AssessmentIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {stats.total}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Servicios
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
        <Card 
          sx={{ 
            bgcolor: '#e8f5e8', 
            borderLeft: '4px solid #4caf50',
            height: '120px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              boxShadow: 3,
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}
        >
          <CardContent sx={{ width: '100%', textAlign: 'center' }}>
            <Typography variant="h6" color="#4caf50">Activos</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.active}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
        <Card 
          sx={{ 
            bgcolor: '#ffebee', 
            borderLeft: '4px solid #f44336',
            height: '120px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              boxShadow: 3,
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}
        >
          <CardContent sx={{ width: '100%', textAlign: 'center' }}>
            <Typography variant="h6" color="#f44336">Inactivos</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.inactive}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
        <Card 
          sx={{ 
            bgcolor: '#fff3e0', 
            borderLeft: '4px solid #ff9800',
            height: '120px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              boxShadow: 3,
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}
        >
          <CardContent sx={{ width: '100%', textAlign: 'center' }}>
            <Typography variant="h6" color="#ff9800">Precio Promedio</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{formatCurrency(stats.avgPrice)}</Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
    {/* Tabla de Servicios */}
    <Paper elevation={3}>
      <Box p={2}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Listado de Servicios
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          Para editar un servicio, haz clic en el <strong>nombre del servicio</strong> o en el ícono de lápiz — ambos abren la misma ventana, con pestañas para Información General, Subservicios, Cobertura del Paquete y Capacitaciones por Defecto. Un paquete "requiere capacitación" automáticamente en cuanto tiene al menos una plantilla configurada ahí.
        </Alert>

        {/* Botones de Acción */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            onClick={() => setCreateDialogOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            + Agregar Servicio
          </Button>
          <Button
            variant="outlined"
            onClick={fetchServices}
            startIcon={<RefreshIcon />}
            sx={{ textTransform: 'none' }}
          >
            Actualizar
          </Button>
        </Box>
        
        {/* Filtros */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Filtrar por Nombre"
              value={filters.serviceName}
              onChange={(e) => handleFilterChange('serviceName', e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Filtrar por Prefijo"
              value={filters.folioPrefix}
              onChange={(e) => handleFilterChange('folioPrefix', e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.active}
                label="Estado"
                onChange={(e) => handleFilterChange('active', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Activos</MenuItem>
                <MenuItem value="false">Inactivos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Rango de Precio</InputLabel>
              <Select
                value={filters.priceRange}
                label="Rango de Precio"
                onChange={(e) => handleFilterChange('priceRange', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="0-1000">$0 - $1,000</MenuItem>
                <MenuItem value="1000-5000">$1,000 - $5,000</MenuItem>
                <MenuItem value="5000-10000">$5,000 - $10,000</MenuItem>
                <MenuItem value="10000">$10,000+</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Prefijo Folio</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Precio</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Descuento</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>IVA</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nivel de Riesgo</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredServices.map((service) => (
              <TableRow 
                key={service.id}
                data-service-id={service.id}
                hover
                title="Doble clic para abrir"
                sx={{ '&:hover': { bgcolor: '#f9f9f9' }, cursor: 'pointer' }}
                onDoubleClick={handleCellClick}
                className={selectedService?.id === service.id ? 'selected' : ''}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      {service.serviceName}
                    </Typography>
                    {service.hasDefaultTrainings && (
                      <Tooltip title="Requiere capacitación: tiene al menos un curso configurado en la pestaña Capacitaciones por Defecto">
                        <Chip
                          icon={<SchoolOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                          label="Capacitación"
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            height: 22,
                            bgcolor: '#0288d1',
                            color: 'white',
                            '& .MuiChip-icon': { color: 'white' },
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{service.folioPrefix || 'N/A'}</TableCell>
                <TableCell>{formatCurrency(service.price)}</TableCell>
                <TableCell>{service.discount || 0}%</TableCell>
                <TableCell>{service.iva || 0}%</TableCell>
                <TableCell>{formatCurrency(service.subtotal)}</TableCell>
                <TableCell>
                  {service.riskLevel ? (
                    <Chip
                      label={RISK_LEVEL_LABELS[service.riskLevel] || service.riskLevel}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: RISK_LEVEL_COLORS[service.riskLevel]?.bg || '#eeeeee',
                        color: RISK_LEVEL_COLORS[service.riskLevel]?.text || '#616161'
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell key={`toggle-${service.id}-${service.active}`}>
                  <IconButton 
                    size="small" 
                    onClick={(e) => { e.stopPropagation(); handleToggleActive(service); }}
                    sx={{ 
                      color: service.active ? '#4caf50' : '#f44336',
                      '&:hover': {
                        backgroundColor: service.active ? '#e8f5e8' : '#ffebee'
                      }
                    }}
                    title={service.active ? 'Desactivar servicio' : 'Activar servicio'}
                  >
                    {service.active ? <ToggleOnIcon /> : <ToggleOffIcon />}
                  </IconButton>
                  <Typography 
                    variant="body2" 
                    component="span"
                    sx={{ 
                      color: service.active ? '#4caf50' : '#f44336',
                      fontWeight: 'bold',
                      ml: 1
                    }}
                  >
                    {service.active ? 'Activo' : 'Inactivo'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleEditClick(service); }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteClick(service); }}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredServices.length === 0 && services.length > 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                    No se encontraron servicios con los filtros aplicados
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {services.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                    No hay servicios disponibles
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>

    <Dialog
      open={createDialogOpen}
      onClose={() => {
        setCreateDialogOpen(false);
        resetForm();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        } 
      }}
    >
      <DialogTitle 
        sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 2.5
        }}
      >
        <AddCircleOutlineIcon sx={{ fontSize: 32 }} />
        Agregar Nuevo Servicio
      </DialogTitle>
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
        
        {/* Sección: Información General */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600, 
              color: '#1976d2',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <BusinessIcon /> Información General
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="Nombre del Servicio"
                value={editService.serviceName}
                onChange={(e) => setEditService({ ...editService, serviceName: e.target.value })}
                placeholder="Ej: Programa de Protección Civil"
                required
                InputProps={{
                  startAdornment: <BusinessIcon sx={{ mr: 1, color: '#1976d2' }} />
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1976d2'
                    }
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Prefijo del Folio"
                value={editService.folioPrefix}
                onChange={(e) => setEditService({ ...editService, folioPrefix: e.target.value })}
                placeholder="Ej: PPC"
                required
                InputProps={{
                  startAdornment: <ReceiptIcon sx={{ mr: 1, color: '#1976d2' }} />
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1976d2'
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Sección: Información Financiera */}
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600, 
              color: '#4caf50',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <AttachMoneyIcon /> Información Financiera
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Precio Base"
                type="number"
                value={editService.price}
                onChange={(e) => setEditService({ ...editService, price: e.target.value })}
                placeholder="0.00"
                required
                InputProps={{
                  startAdornment: <AttachMoneyIcon sx={{ mr: 1, color: '#4caf50' }} />
                }}
                helperText="Precio antes de descuentos e impuestos"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#4caf50'
                    }
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Descuento (%)"
                type="number"
                value={editService.discount}
                onChange={(e) => setEditService({ ...editService, discount: e.target.value })}
                placeholder="0"
                InputProps={{
                  startAdornment: <PercentIcon sx={{ mr: 1, color: '#ff9800' }} />
                }}
                helperText="Porcentaje de descuento aplicable (opcional)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#ff9800'
                    }
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Subtotal (Calculado)"
                value={formatCurrency(calculateSubtotal(editService.price, editService.discount))}
                disabled
                InputProps={{
                  startAdornment: <AttachMoneyIcon sx={{ mr: 1, color: '#2196f3' }} />
                }}
                helperText="Precio - Descuento (IVA 16% fijo)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Sección: Nivel de Riesgo */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: '#9c7c38',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <WarningAmberIcon /> Nivel de Riesgo
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="Nivel de Riesgo"
                value={editService.riskLevel || ''}
                onChange={(e) => setEditService({ ...editService, riskLevel: e.target.value })}
                helperText="El ente hereda este riesgo al contratar el servicio. 'Sin definir' si no aplica (ej. Dictamen Eléctrico)."
              >
                <MenuItem value=""><em>Sin definir</em></MenuItem>
                <MenuItem value="BAJO">Bajo</MenuItem>
                <MenuItem value="MEDIO">Medio</MenuItem>
                <MenuItem value="ALTO">Alto</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Box>

        {/* Nota informativa */}
        <Alert
          severity="info"
          sx={{ 
            mt: 2,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              fontSize: 24
            }
          }}
        >
          <Typography variant="body2">
            <strong>Nota:</strong> El servicio se creará en estado <strong>activo</strong> por defecto con <strong>IVA fijo del 16%</strong>.
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f5f5f5' }}>
        <Button 
          onClick={() => {
            setCreateDialogOpen(false);
            resetForm();
          }}
          sx={{ 
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleCreateSubmit} 
          variant="contained" 
          startIcon={<AddCircleOutlineIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              boxShadow: '0 6px 16px rgba(25,118,210,0.4)'
            }
          }}
        >
          Crear Servicio
        </Button>
      </DialogActions>
    </Dialog>
      {modalOpen && selectedService && (
      <ModalServicio
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedService(null);
          setSelectedTab(0);
          clearEditServiceParam();
        }}
        service={selectedService}
        selectedTab={selectedTab}
        onSaved={(updated) => {
          const updatedServices = services.map(s => s.id === updated.id ? updated : s);
          setServices(updatedServices);
          setFilteredServices(updatedServices);
          calculateStats(updatedServices);
          setSelectedService(updated);
        }}
      />
      )}
    </Box>
  );
};

export default CatalogoServicios;